/**
 * Subsidie-evaluatiekern — de pure rekenlogica achter `useEvaluateSubsidies`.
 *
 * Deze laag is bewust vrij van React en Supabase: hij importeert uitsluitend
 * types, en die verdwijnen bij het compileren. Daardoor kan
 * `tests/subsidie/evaluatie.node.test.mjs` hem rechtstreeks laden zonder de
 * app, de router of een databaseverbinding te starten.
 *
 * De reparaties in dit bestand komen uit de meting van 8 september 2026
 * (rapporten `rvo/mia-vamil.md`, `rvo/eia.md` en `rvo/flex-e.md`), uitgevoerd op
 * de echte intakes 6daab7ab (investering 1.350.000, mkb, VPB 19%) en 717e4678
 * (investering 99.981, batterij 80.000, EMS 10.000, batterij_kw 200).
 *
 * Gemeten uitkomsten, oud → ronde één → nu:
 * - MIA/VAMIL op 6daab7ab: 297.270 → 89.505 → 89.505 (letter onbekend;
 *   met letter F 135.675, met letter C 20.250 = alleen Vamil).
 * - Flex-E op 717e4678: 39.992 → 131.991 → 36.000.
 *
 * De sprong naar 131.991 in ronde één was 132% van de investering: het gedeelde
 * percentageplafond werd losgelaten zonder dat de grondslagen eronder klopten.
 * Ronde twee repareert die grondslagen zelf.
 */
import type { SubsidieIntakeData } from './useSubsidieIntake';
import type { SubsidieRegeling, SubsidieRegel, SubsidieBedrag } from './useSubsidieRegelingen';

/**
 * De vier statussen die `sub_matches.match_status` toestaat. De kolom heeft een
 * CHECK-constraint op precies deze waarden
 * (`supabase/migrations/20260408120000_subsidie_module_tabellen_en_seed.sql:143-145`),
 * dus een vijfde waarde toevoegen breekt de insert én `STATUS_CONFIG` in
 * `SubsidieDashboard.tsx:131`. Zie `evaluateAllSubsidies` voor hoe "ongetoetst"
 * binnen deze vier waarden wordt uitgedrukt.
 */
export type SubsidieMatchStatus = 'volledig' | 'gedeeltelijk' | 'niet_voldaan' | 'niet_relevant';

export interface SubsidieEvaluatieResultaat {
  regeling: SubsidieRegeling;
  match_status: SubsidieMatchStatus;
  geschat_totaal: number;
  berekening: Record<string, number>;
  onvervulde_regels: Array<{ beschrijving: string; foutmelding: string }>;
  aanbevelingen: string[];
  /**
   * True wanneer er voor deze regeling géén enkele toetsingsregel is
   * vastgelegd. Het bedrag is dan een rekensom zonder toets — geen match.
   * Losse vlag omdat `match_status` geen vijfde waarde mag krijgen.
   */
  ongetoetst: boolean;
}

// --- Rule evaluation engine (client-side) ---

/**
 * Leest `sub_regels.waarde` (jsonb) om naar de waarde waartegen we toetsen.
 *
 * Hier stond `typeof waarde === 'string' ? JSON.parse(waarde) : waarde`, kaal en
 * zonder try/catch. Dat berust op de aanname dat een string altijd JSON is, en
 * die klopt niet: een jsonb-kolom die een tekstwaarde bevat geeft in JavaScript
 * gewoon een string terug. `JSON.parse('bedrijventerrein')` gooit een
 * SyntaxError, en die liep tot in de React-render.
 *
 * Vandaag crashte er niets omdat alle vijftien bestaande regels een array of een
 * getal dragen. Maar het beheerscherm (T7.4) laat een mens een tekstregel
 * aanmaken, en dan is de eerste toetsing de laatste. Een regel die verkeerd
 * staat hoort `false` op te leveren, niet het scherm om te trekken.
 *
 * Dubbel gecodeerde waarden blijven werken: die parsen wél, en dan wint het
 * geparste resultaat.
 */
function leesRegelWaarde(waarde: unknown): unknown {
  if (typeof waarde !== 'string') return waarde;
  try {
    return JSON.parse(waarde);
  } catch {
    return waarde;
  }
}

export function evaluateRule(data: SubsidieIntakeData, regel: SubsidieRegel): boolean {
  const value = getNestedValue(data, regel.veld);
  const expected = leesRegelWaarde(regel.waarde);

  switch (regel.operator) {
    case 'is_true':
      return value === true;
    case 'is_false':
      return value === false;
    case 'is_not_null':
      return value != null && (Array.isArray(value) ? value.length > 0 : true);
    case 'eq':
      return value === expected;
    case 'neq':
      return value !== expected;
    case 'gt':
      return typeof value === 'number' && value > Number(expected);
    case 'gte':
      return typeof value === 'number' && value >= Number(expected);
    case 'lt':
      return typeof value === 'number' && value < Number(expected);
    case 'lte':
      return typeof value === 'number' && value <= Number(expected);
    case 'in':
      return Array.isArray(expected) && expected.includes(value);
    case 'not_in':
      return Array.isArray(expected) && !expected.includes(value);
    case 'contains':
      if (Array.isArray(value) && Array.isArray(expected)) {
        return expected.some(e => value.includes(e));
      }
      return false;
    case 'between':
      if (Array.isArray(expected) && expected.length === 2) {
        return typeof value === 'number' && value >= expected[0] && value <= expected[1];
      }
      return false;
    default:
      return false;
  }
}

export function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

// --- VPB-tarief ---

/** Schijfgrens vennootschapsbelasting 2026: 19% t/m € 200.000 winst, daarboven 25,8%. */
export const VPB_SCHIJFGRENS = 200000;
export const VPB_TARIEF_LAAG = 0.19;
export const VPB_TARIEF_HOOG = 0.258;

/**
 * DEFECT 4 — het wettelijke criterium voor het VPB-tarief is de belastbare
 * winst (grens € 200.000), niet de bedrijfsgrootte. De oude `getVpbTarief`
 * koos 19% zodra `bedrijfsgrootte` 'klein' of 'mkb' was; een winstgevende
 * mkb'er met meer dan € 200.000 winst kreeg daardoor 19% toegerekend en zag
 * zijn EIA/MIA-voordeel te laag ingeschat (gemeten verschil op een aftrek van
 * € 540.000: 19% → 102.600 tegen 25,8% → 139.320).
 *
 * Volledig repareren kan vandaag niet: `SubsidieIntakeData`
 * (`src/hooks/useSubsidieIntake.ts:6-58`) kent geen winstveld. Het benodigde
 * veld is `belastbare_winst?: number`. Zodra dat er staat, rekent deze functie
 * er vanzelf mee; zolang het ontbreekt blijft de oude bedrijfsgrootte-schatting
 * staan, zodat er vandaag geen enkel getal verandert.
 */
export function resolveVpbTarief(data: SubsidieIntakeData): number {
  const winst = leesOptioneelGetal(data, 'belastbare_winst');
  if (winst != null) {
    return winst > VPB_SCHIJFGRENS ? VPB_TARIEF_HOOG : VPB_TARIEF_LAAG;
  }
  const grootte = data.bedrijfsgrootte ?? 'mkb';
  return (grootte === 'klein' || grootte === 'mkb') ? VPB_TARIEF_LAAG : VPB_TARIEF_HOOG;
}

/**
 * VAMIL is geen aftrek maar een liquiditeitsvoordeel. De 2% tijdswaarde in deze
 * formule is een aanname uit de oorspronkelijke code; het RVO-onderzoek van
 * 8-9-2026 vond er geen bron voor (rapport `rvo/mia-vamil.md`, §5). Bewust
 * ongewijzigd gelaten: het getal verzinnen of aanpassen zou een nieuwe,
 * even onbewezen aanname zijn.
 */
export const VAMIL_TIJDSWAARDE = 0.02;

// --- Grondslag per bedrag-record ---

/**
 * DEFECT 3 — de grondslag van een bedrag is instelbaar per bedrag-record, en de
 * sleutel is `categorie/subcategorie`, niet alleen `categorie`.
 *
 * De aftrek-tak rekende blind met `investering_totaal`: netaansluiting, civiel
 * werk én laadpalen. Het EIA-onderzoek van 8-9-2026 stelde vast dat
 * laadinfrastructuur helemaal niet op de Energielijst 2026 staat en een
 * zelfstandig EMS evenmin; op een laadplein van € 1 mln met € 200k batterij
 * meldt het CRM circa € 103.000 EIA waar circa € 21.000 realistisch is.
 *
 * Die conclusie is NIET verwerkt: het rapport vraagt er zelf bevestiging op en
 * die is er nog niet. Wat hier wél gebeurt: de grondslag komt uit het
 * bedrag-record. Voor de aftrek-rijen verandert dat vandaag geen cent — geen
 * enkele bestaande aftrek-rij noemt een sleutel uit deze tabel (EIA =
 * 'energie_investering', MIA = 'mia'/'vamil'), dus die vallen terug op
 * `investering_totaal` en houden hun gemeten uitkomsten (EIA 102.600, MIA
 * 89.505 op intake 6daab7ab).
 *
 * De sleutel is eerst `categorie/subcategorie` en pas daarna `categorie`. Dat
 * was het gemiste deel van deze haak: een toekomstige EIA-rij die per
 * Energielijst-code een andere grondslag nodig heeft ('batterij/260101') past
 * er nu in, zonder de categorie-brede regel te breken.
 *
 * Nog niet af te dekken: een 'zon_pv'-rij. De intake kent wel `pv_kwp` maar geen
 * `investering_pv`; zonder dat veld zou zo'n rij op `investering_totaal`
 * terugvallen en dus fout rekenen.
 */
export const GRONDSLAG_PER_SLEUTEL: Record<string, string[]> = {
  // Aftrek-kant (EIA/MIA): staat klaar, wordt vandaag door geen enkele rij geraakt.
  batterij: ['investering_batterij'],
  ems: ['investering_ems'],
  laadinfra: ['investering_laadinfra'],

  /**
   * Flex-E-kant. RVO beoordeelt de drie onderdelen los en meet elk over ZIJN
   * EIGEN subsidiabele kosten (`rvo/flex-e.md` §4): de scan over de kosten van
   * de scan, de haalbaarheidsstudie over de kosten van de studie, de realisatie
   * over de kosten van de flexibiliteitsmaatregel. De oude kern rekende scan én
   * studie over `investering_totaal`; dat is de motor achter de overschatting
   * die op intake 717e4678 gemeten is (zie DEFECT 6 hieronder).
   *
   * `kosten_flexibiliteitsscan` en `kosten_haalbaarheidsstudie` bestaan vandaag
   * NIET in `SubsidieIntakeData` (`src/hooks/useSubsidieIntake.ts:6-58`). Dat is
   * geen verzinsel maar de kern van het probleem: er is geen kostenbasis voor
   * deze twee onderdelen, dus er valt niets te schatten. Ze leveren daarom € 0
   * plus een aanbeveling die zegt welk veld ontbreekt — onderschatten boven
   * overschatten, dezelfde keuze als bij `kiesAftrekSchijf`.
   */
  realisatie: ['investering_batterij', 'investering_ems'],
  scan: ['kosten_flexibiliteitsscan'],
  haalbaarheidsstudie: ['kosten_haalbaarheidsstudie'],
};

export interface Grondslag {
  /** De intake-velden die samen de basis vormen, elk met zijn waarde. */
  delen: Array<{ veld: string; waarde: number }>;
  /** De som van `delen`. */
  waarde: number;
  /** Leesbare naam van de grondslag, velden met '+' ertussen. */
  veld: string;
  /** True = het bedrag-record wijst een eigen grondslag aan; false = terugval. */
  expliciet: boolean;
  /** True = geen van de aangewezen velden staat in de intake. */
  ontbreekt: boolean;
}

export function resolveGrondslag(data: SubsidieIntakeData, bedrag: SubsidieBedrag): Grondslag {
  const sleutels = [
    bedrag.subcategorie ? `${bedrag.categorie}/${bedrag.subcategorie}` : null,
    bedrag.categorie,
  ].filter(Boolean) as string[];

  for (const sleutel of sleutels) {
    const velden = GRONDSLAG_PER_SLEUTEL[sleutel];
    if (!velden) continue;
    const delen = velden.map(veld => ({ veld, waarde: leesOptioneelGetal(data, veld) ?? 0 }));
    return {
      delen,
      waarde: delen.reduce((som, d) => som + d.waarde, 0),
      veld: velden.join(' + '),
      expliciet: true,
      ontbreekt: velden.every(veld => leesOptioneelGetal(data, veld) == null),
    };
  }

  const waarde = data.investering_totaal ?? 0;
  return {
    delen: [{ veld: 'investering_totaal', waarde }],
    waarde,
    veld: 'investering_totaal',
    expliciet: false,
    ontbreekt: data.investering_totaal == null,
  };
}

// --- Keuze van één aftrekschijf ---

/**
 * Leest de Milieulijst-letters uit een subcategorie-label. 'categorie_a_d' →
 * ['A','D'], 'categorie_f_g' → ['F','G'], 'categorie_b' → ['B']. Labels zonder
 * letters ('<100kw', '260101') leveren een lege lijst en doen dus niet mee.
 *
 * LET OP: het label is NIET volledig. De 27%-rij heet `categorie_b` maar dekt
 * blijkens haar eigen opmerkingen 'MIA 27% — Milieulijst letter B/E' ook letter
 * E. Gebruik daarom `milieulijstLettersVanBedrag`, niet deze functie los.
 */
export function milieulijstLettersVanSubcategorie(subcategorie: string | null): string[] {
  if (!subcategorie) return [];
  return subcategorie
    .replace(/^categorie[_-]/i, '')
    .split(/[_\-\s]+/)
    .filter(deel => /^[a-g]$/i.test(deel))
    .map(deel => deel.toUpperCase());
}

/**
 * Alle Milieulijst-letters die bij één bedrag-rij horen: uit het label én uit de
 * opmerkingen, samengevoegd.
 *
 * Waarom allebei. Het label van de 27%-rij is `categorie_b`, maar haar
 * opmerkingen zeggen 'MIA 27% — Milieulijst letter B/E'. Wie alleen het label
 * leest, kent letter E niet en geeft een investering met letter E nul MIA —
 * gemeten op intake 6daab7ab: 20.250 (alleen Vamil) waar 89.505 hoort te staan.
 * Dat is precies het soort onderschatting dat net zo fout is als de stapeling
 * die we hebben weggehaald, alleen de andere kant op.
 *
 * De opmerkingen zijn hier de betrouwbaarder bron omdat ze de letters uitschrijven;
 * het label kort ze af en verliest er daarbij een. Zodra `sub_bedragen` ooit een
 * eigen letterkolom krijgt, is dat de plek en vervalt dit lezen uit proza.
 */
export function milieulijstLettersVanBedrag(bedrag: {
  subcategorie: string | null;
  opmerkingen?: string | null;
}): string[] {
  const uitLabel = milieulijstLettersVanSubcategorie(bedrag.subcategorie);

  // 'Milieulijst letter B/E' of 'letter A/D' — pak wat er na 'letter' komt.
  const uitProza: string[] = [];
  const tekst = bedrag.opmerkingen ?? '';
  const match = tekst.match(/letter\s+([A-G](?:\s*[\/,&+-]\s*[A-G])*)/i);
  if (match) {
    for (const deel of match[1].split(/[\/,&+\s-]+/)) {
      const letter = deel.trim().toUpperCase();
      if (/^[A-G]$/.test(letter)) uitProza.push(letter);
    }
  }

  return [...new Set([...uitLabel, ...uitProza])].sort();
}

/** Leest een eventueel toekomstig intake-veld `milieulijst_letter` ('A' t/m 'G'). */
export function leesMilieulijstLetter(data: SubsidieIntakeData): string | null {
  const ruw = getNestedValue(data, 'milieulijst_letter');
  if (typeof ruw !== 'string') return null;
  const letter = ruw.trim().toUpperCase();
  return /^[A-G]$/.test(letter) ? letter : null;
}

/**
 * DEFECT 1 — per regeling en per categorie vuurt hoogstens ÉÉN aftrekschijf.
 *
 * De oude aftrek-tak las `b.subcategorie` nergens; de enige filter was
 * bedrijfsgrootte, en die is bij alle vier de MIA/VAMIL-bedragen null. Gevolg:
 * categorie_b (27%), categorie_a_d (36%) én categorie_f_g (45%) vuurden alle
 * drie op dezelfde investering: 27 + 36 + 45 = 108% aftrek. Gemeten op de echte
 * intake (investering 1.350.000, VPB 19%): 69.255 + 92.340 + 115.425 + 20.250
 * VAMIL = 297.270, waar 135.675 (alleen F/G plus VAMIL) het maximum is —
 * factor 2,19. Het plafond van 45% beet niet, want dat rekende 45% van de
 * INVESTERING (607.500), niet van de aftrek.
 *
 * Eén bedrijfsmiddel heeft één Milieulijst-letter en dus één percentage. De
 * keuze:
 *
 * - Is de letter bekend (intake-veld `milieulijst_letter`), dan wint de schijf
 *   waar die letter in staat. Dat veld bestaat vandaag nog niet in
 *   `SubsidieIntakeData`; deze functie leest het defensief, zodat het werkt
 *   zodra het er is.
 * - Is de letter onbekend, dan wint de LAAGSTE schijf. Reden: overschatten is
 *   duurder dan onderschatten. Een te hoge fiscale aftrek belandt via
 *   `scenario-to-businesscase.ts` in offertes en businesscases; die moet de
 *   klant later terugnemen. Met de laagste schijf komt de meting uit op
 *   69.255 + 20.250 = 89.505 in plaats van 297.270 — onder het maximum van
 *   135.675, dus veilig aan de lage kant.
 * - Noemt de intake een letter die in geen enkele schijf voorkomt, dan vuurt er
 *   GEEN schijf. Dat is de correctie op ronde één, die hier terugviel op de
 *   laagste schijf "omdat een typefout een regeling niet mag laten verdwijnen".
 *   Het veld laat alleen A t/m G toe en die zeven letters zijn allemaal echte
 *   Milieulijst-letters — er is dus geen typefout om op te vangen. Letter C is
 *   het gemeten geval: C heeft wél Vamil maar GEEN MIA-percentage
 *   (`rvo/mia-vamil.md` §3 — MIA bestaat alleen bij A/D 36%, B/E 27%, F/G 45%).
 *   Met de terugval meldde de kern op intake 6daab7ab 69.255 MIA bij letter C,
 *   waar RVO 0 geeft; de Vamil van 20.250 blijft wel staan, want die hangt aan
 *   een eigen bedrag-rij zonder letter. De notitie zegt de gebruiker waarom.
 */
export function kiesAftrekSchijf(
  kandidaten: SubsidieBedrag[],
  data: SubsidieIntakeData,
): { gekozen: SubsidieBedrag | null; notitie: string | null } {
  if (kandidaten.length === 0) return { gekozen: null, notitie: null };

  // Bewust GEEN kortsluiting op één kandidaat. Die stond hier, en dan vuurde de
  // enige schijf-rij ongeacht de letter — de letterguard hield alleen stand
  // doordat de database toevallig drie MIA-rijen heeft. Een bedrijfsgrootte-
  // filter dat de kandidaten tot één rij terugbrengt, zette hem al buitenspel.

  // Deterministische volgorde: laagste percentage eerst, dan alfabetisch op
  // subcategorie zodat twee gelijke percentages altijd dezelfde winnaar geven.
  const gesorteerd = [...kandidaten].sort(
    (a, b) =>
      a.bedrag - b.bedrag ||
      String(a.subcategorie ?? '').localeCompare(String(b.subcategorie ?? '')),
  );

  const letter = leesMilieulijstLetter(data);

  // De letterguard geldt alleen binnen een groep die ÓP letters is gesleuteld.
  // Vamil draagt geen enkele letter (subcategorie null, geen letter in de
  // opmerkingen), dus een opgegeven letter zegt niets over die rij en mag hem
  // niet uitsluiten. Zonder deze controle liet letter C het Vamil-voordeel van
  // 20.250 verdwijnen — een fout die pas ontstond toen de kortsluiting op één
  // kandidaat eruit ging, en die de test meteen ving.
  const groepKentLetters = gesorteerd.some(k => milieulijstLettersVanBedrag(k).length > 0);

  if (letter && groepKentLetters) {
    const passend = gesorteerd.filter(k => milieulijstLettersVanBedrag(k).includes(letter));
    if (passend.length > 0) return { gekozen: passend[0], notitie: null };

    // De letter is geldig maar hoort bij geen enkele schijf: geen aftrek.
    const bekend = [...new Set(gesorteerd.flatMap(k => milieulijstLettersVanBedrag(k)))]
      .sort()
      .join(', ');
    return {
      gekozen: null,
      notitie:
        `Milieulijst-letter ${letter} heeft geen ${kandidaten[0].categorie.toUpperCase()}-percentage — `
        + `alleen ${bekend} kennen er een. Er is dus geen ${kandidaten[0].categorie.toUpperCase()}-aftrek gerekend. `
        + 'Controleer de letter op de Milieulijst; een Vamil-voordeel kan er wél zijn.',
    };
  }

  return { gekozen: gesorteerd[0], notitie: null };
}

// --- Keuze tussen elkaar uitsluitende vermogensbanden ---

/**
 * Herkent een subcategorie die een vermogensband is: '<100kw', '>=100kw'.
 * Bewust streng verankerd aan het begin van de tekst, zodat de SPULA- en
 * SPRILA-labels ('200_350kw', 'dc_>=100kw', 'ac_duo_>=2x11kw') er NIET onder
 * vallen. Die horen bij `matchesVermogenRange`, waar meerdere rijen naast elkaar
 * mogen vuren omdat een laadplein laadstations van verschillend vermogen heeft.
 */
const VERMOGENSBAND = /^(<=|>=|<|>)\s*(\d+(?:[.,]\d+)?)\s*kw$/i;

export function leesVermogensband(
  subcategorie: string | null,
): { operator: string; kw: number } | null {
  if (!subcategorie) return null;
  const match = VERMOGENSBAND.exec(subcategorie.trim());
  if (!match) return null;
  return { operator: match[1], kw: Number(match[2].replace(',', '.')) };
}

function voldoetAanVermogensband(kw: number, band: { operator: string; kw: number }): boolean {
  switch (band.operator) {
    case '<': return kw < band.kw;
    case '<=': return kw <= band.kw;
    case '>': return kw > band.kw;
    case '>=': return kw >= band.kw;
    default: return false;
  }
}

/**
 * DEFECT 6a — twee elkaar uitsluitende rijen vuurden allebei.
 *
 * Flex-E heeft voor het realisatiedeel twee bedrag-rijen: subcategorie '<100kw'
 * en '>=100kw' (`rvo/flex-e.md` §4 — de tweedeling op 100 kW staat overeind, met
 * het congestiemanagementcontract als verschil). Eén flexibiliteitsmaatregel valt
 * in precies één van die twee. De percentage-tak las `subcategorie` niet, dus op
 * intake 717e4678 telde de kern 2 × € 36.000 waar € 36.000 hoort te staan —
 * dezelfde soort dubbeltelling als de MIA-schijven in DEFECT 1.
 *
 * Het vermogen van de maatregel komt uit `batterij_kw`
 * (`SubsidieIntakeData:41`); dat is vandaag de enige flexibiliteitsmaatregel die
 * de intake in kW uitdrukt. Gemeten: intake 717e4678 heeft `batterij_kw` 200 en
 * 6daab7ab heeft 250 — beide dus '>=100kw'. Is het vermogen onbekend, dan wint
 * de laagste band-rij (bij gelijk percentage de alfabetisch eerste), zodat het
 * antwoord deterministisch is en nooit hoger uitvalt dan de andere band.
 *
 * Let op de grens: een niet-batterij-flexibiliteitsmaatregel (procesflexibiliteit,
 * warmtebuffer) heeft geen kW-veld in de intake en belandt daarom in de
 * onbekend-tak.
 */
export function kiesVermogensbandRij(
  kandidaten: SubsidieBedrag[],
  data: SubsidieIntakeData,
): SubsidieBedrag | null {
  const metBand = kandidaten.filter(k => leesVermogensband(k.subcategorie) != null);
  if (metBand.length < 2) return null;

  const gesorteerd = [...metBand].sort(
    (a, b) =>
      a.bedrag - b.bedrag ||
      String(a.subcategorie ?? '').localeCompare(String(b.subcategorie ?? '')),
  );

  const kw = leesOptioneelGetal(data, 'batterij_kw');
  if (kw != null) {
    const passend = gesorteerd.find(k => voldoetAanVermogensband(kw, leesVermogensband(k.subcategorie)!));
    if (passend) return passend;
  }

  return gesorteerd[0];
}

// --- Amount calculation engine ---

export function calculateAmount(
  data: SubsidieIntakeData,
  regeling: SubsidieRegeling,
  bedragen: SubsidieBedrag[],
): { totaal: number; breakdown: Record<string, number>; notities: string[] } {
  const grootte = data.bedrijfsgrootte ?? 'mkb';
  // Filter op bedrijfsgrootte gebeurt vóór de schijfkeuze, zodat er gekozen
  // wordt uit de rijen die daadwerkelijk in aanmerking komen.
  const regelingBedragen = bedragen.filter(
    b => b.regeling_id === regeling.id && (!b.bedrijfsgrootte || b.bedrijfsgrootte === grootte),
  );
  const notities: string[] = [];

  // DEFECT 1: bepaal per categorie welke ene aftrekschijf mag vuren.
  const aftrekPerCategorie = new Map<string, SubsidieBedrag[]>();
  for (const b of regelingBedragen) {
    if (b.bedrag_type !== 'aftrek') continue;
    const lijst = aftrekPerCategorie.get(b.categorie) ?? [];
    lijst.push(b);
    aftrekPerCategorie.set(b.categorie, lijst);
  }
  const gekozenAftrekIds = new Set<string>();
  for (const kandidaten of aftrekPerCategorie.values()) {
    const { gekozen, notitie } = kiesAftrekSchijf(kandidaten, data);
    if (gekozen) gekozenAftrekIds.add(gekozen.id);
    if (notitie) notities.push(notitie);
  }

  // DEFECT 6a: bepaal per categorie welke ene vermogensband-rij mag vuren.
  const percentagePerCategorie = new Map<string, SubsidieBedrag[]>();
  for (const b of regelingBedragen) {
    if (b.bedrag_type !== 'percentage') continue;
    const lijst = percentagePerCategorie.get(b.categorie) ?? [];
    lijst.push(b);
    percentagePerCategorie.set(b.categorie, lijst);
  }
  const afgevallenBandIds = new Set<string>();
  for (const kandidaten of percentagePerCategorie.values()) {
    const gekozen = kiesVermogensbandRij(kandidaten, data);
    if (!gekozen) continue;
    for (const k of kandidaten) {
      if (k.id !== gekozen.id && leesVermogensband(k.subcategorie)) afgevallenBandIds.add(k.id);
    }
  }

  const breakdown: Record<string, number> = {};
  const vpb = resolveVpbTarief(data);
  // Welke intake-velden dit bedrag hebben gedragen — voedt de sanity-grens
  // onderaan deze functie.
  const gebruikteGrondslagen = new Map<string, number>();
  // DEFECT 6: twee potjes. `gedeeld` zijn de onderdelen zonder eigen maximum —
  // die vallen samen onder `subsidie_percentage_max`. `eigenMaximum` zijn de
  // onderdelen die hun eigen `max_bedrag` dragen; die staan los.
  let gedeeld = 0;
  let eigenMaximum = 0;

  for (const b of regelingBedragen) {
    // DEFECT 1: alleen de gekozen schijf telt mee.
    if (b.bedrag_type === 'aftrek' && !gekozenAftrekIds.has(b.id)) continue;
    // DEFECT 6a: van twee elkaar uitsluitende vermogensbanden vuurt er één.
    if (afgevallenBandIds.has(b.id)) continue;

    let amount = 0;
    const label = [b.categorie, b.subcategorie].filter(Boolean).join(' - ');
    let grondslagVanDitBedrag: Grondslag | null = null;

    switch (b.bedrag_type) {
      case 'vast': {
        const stations = data.laadstations ?? [];
        const count = stations.reduce((n, s) => {
          if (matchesVermogenRange(s.vermogen_kw, s.type, b.subcategorie)) {
            return n + s.aantal;
          }
          return n;
        }, 0);
        amount = count * b.bedrag;
        break;
      }
      case 'per_kwh': {
        if (b.categorie === 'batterij' && data.batterij_kwh) {
          const kwh = Math.min(data.batterij_kwh, b.max_eenheden ?? Infinity);
          amount = kwh * b.bedrag;
        }
        break;
      }
      case 'percentage': {
        if (b.categorie.startsWith('truck_')) {
          // AanZET: per truck (percentage of chassis price). Grondslag is de
          // verkoopprijs per truck, niet een intake-breed investeringsveld.
          const trucks = data.trucks_gepland ?? [];
          for (const truck of trucks) {
            if (matchesTruckCategory(truck, b.categorie)) {
              let truckAmount = (truck.verkoopprijs ?? 0) * b.bedrag;
              if (b.max_bedrag) truckAmount = Math.min(truckAmount, b.max_bedrag);
              amount += truckAmount;
            }
          }
          break;
        }

        // DEFECT 6b: percentage over de EIGEN grondslag van het onderdeel.
        // Scan en haalbaarheidsstudie rekenden over `investering_totaal`; op
        // intake 717e4678 leverde dat 10.000 + 49.991 bovenop een realisatiedeel
        // van 36.000. Nu wijst `GRONDSLAG_PER_SLEUTEL` per onderdeel een eigen
        // kostenveld aan; ontbreekt dat veld, dan vuurt het onderdeel niet.
        const grondslag = resolveGrondslag(data, b);
        grondslagVanDitBedrag = grondslag;
        if (grondslag.expliciet && grondslag.ontbreekt) {
          notities.push(
            `Onderdeel "${label}" is op € 0 gezet: de subsidiabele kosten ervan staan niet in de intake `
            + `(veld ${grondslag.veld}). RVO meet dit onderdeel over zijn eigen kosten, niet over de totale `
            + 'investering — een schatting over het projecttotaal zou het bedrag opblazen.',
          );
          break;
        }
        if (b.min_eenheden && grondslag.waarde < b.min_eenheden) break;
        amount = grondslag.waarde * b.bedrag;
        if (b.max_bedrag) amount = Math.min(amount, b.max_bedrag);
        break;
      }
      case 'aftrek': {
        // DEFECT 3: grondslag uit het bedrag-record, met terugval op
        // investering_totaal zodat de bestaande rijen identiek blijven rekenen.
        const grondslag = resolveGrondslag(data, b);
        grondslagVanDitBedrag = grondslag;
        if (grondslag.waarde <= 0) break;

        // DEFECT 5: de EIA/MIA-drempel van € 2.500 geldt PER BEDRIJFSMIDDEL,
        // niet op de som. De regel die hem vandaag toetst staat in `sub_regels`
        // als `investering_totaal >= 2500`
        // (seed-migratie 20260408120000, EIA-regel 1) en kijkt dus naar het
        // totaal. `regeling.min_subsidiebedrag` is hier niet bruikbaar: die
        // kolom draagt twee betekenissen tegelijk — bij EIA/MIA een minimum
        // INVESTERING (2.500) en bij FLEX_E een minimum SUBSIDIEBEDRAG
        // (25.000). Daarom wordt de drempel hier per bedrag-record gelezen uit
        // `min_eenheden`, precies zoals de percentage-tak dat al doet. Geen
        // enkele bestaande aftrek-rij vult `min_eenheden`, dus vandaag verandert
        // er niets. Let op bij het vullen: `min_eenheden` moet dan euro's zijn,
        // geen kWp — de EIA-voorstel-SQL in rvo/eia.md gebruikt de kolom voor
        // kWp en zou hier dus verkeerd landen.
        if (b.min_eenheden && grondslag.waarde < b.min_eenheden) break;

        if (b.categorie === 'vamil') {
          // VAMIL: liquidity benefit ≈ investment × 75% × 2% (time value)
          amount = grondslag.waarde * b.bedrag * VAMIL_TIJDSWAARDE;
        } else {
          // EIA / MIA: net benefit = investment × aftrek% × VPB rate
          amount = grondslag.waarde * b.bedrag * vpb;
        }
        break;
      }
    }

    if (amount > 0) {
      // OPTELLEN, niet overschrijven. Het label is `categorie - subcategorie`
      // en dat is niet uniek: twee rijen met dezelfde sleutels landen erop.
      // Met een toewijzing verdween de tweede uit de specificatie terwijl hij
      // wél in `totaal` meetelde — gemeten 12-09-2026 met één duplicaat van de
      // SPULA-rij 350_550kw op intake 6daab7ab: totaal 212.000 -> 270.000,
      // breakdown ongewijzigd 172.000. Een bedrag dat nergens te zien is, is
      // niet te controleren.
      breakdown[label] = (breakdown[label] ?? 0) + Math.round(amount);
      if (b.max_bedrag) {
        eigenMaximum += amount;
      } else {
        gedeeld += amount;
      }
      for (const deel of grondslagVanDitBedrag?.delen ?? []) {
        gebruikteGrondslagen.set(deel.veld, deel.waarde);
      }
    }
  }

  /**
   * DEFECT 6 — het percentageplafond stapelde over losse onderdelen.
   *
   * `subsidie_percentage_max` werd toegepast op de SOM van alle bedragen,
   * gemeten over `investering_totaal`. Bij Flex-E (0,40) betekende dat één
   * gedeeld plafond over flexibiliteitsscan (50%, max € 10.000),
   * haalbaarheidsstudie (50%, max € 125.000) en realisatie (40%, max
   * € 300.000) samen, terwijl RVO die drie los beoordeelt en elk een eigen
   * maximum geeft. Voorbeeld: bij een investering van € 100.000 kapte het oude
   * plafond de som af op € 40.000, terwijl de drie onderdelen elk binnen hun
   * eigen maximum blijven.
   *
   * De scheidslijn is het eigen maximum van het onderdeel: een rij met een
   * `max_bedrag` is door RVO al apart begrensd en valt niet onder het gedeelde
   * plafond. Rijen zonder eigen maximum (SPULA 20%, SPRILA 40%, EIA, MIA) delen
   * het plafond wél — daar is `subsidie_percentage_max` een echte
   * regelingbrede grens en blijft het gedrag ongewijzigd.
   *
   * WAARSCHUWING uit ronde twee: dit loslaten is alleen veilig omdat de
   * grondslagen hierboven kloppen. Alle vier de Flex-E-rijen dragen een
   * `max_bedrag`, dus voor Flex-E is `subsidie_percentage_max` hierna dood — en
   * zolang scan en studie nog over `investering_totaal` rekenden en beide
   * realisatie-rijen vuurden, was dit plafond de enige rem. Gemeten op intake
   * 717e4678: 39.992 (met plafond) → 131.991 (zonder plafond, oude grondslagen)
   * → 36.000 (zonder plafond, met de grondslagen en de bandkeuze hierboven).
   * Wie hier een rij toevoegt met `max_bedrag` maar zonder eigen grondslag in
   * `GRONDSLAG_PER_SLEUTEL`, zet die rem opnieuw uit.
   */
  let percentageplafondBeet = false;
  if (regeling.subsidie_percentage_max && data.investering_totaal) {
    const maxFromPercentage = data.investering_totaal * regeling.subsidie_percentage_max;
    if (gedeeld > maxFromPercentage) {
      gedeeld = maxFromPercentage;
      percentageplafondBeet = true;
    }
  }

  /**
   * De sanity-grens die het gedeelde plafond vervangt als laatste rem.
   *
   * Geen enkele regeling betaalt meer uit dan de subsidiabele kosten waarover
   * hij gemeten is. Ronde één liet die rem los en kwam op intake 717e4678 uit op
   * € 131.991 subsidie bij € 99.981 investering — 132%. De echte reparatie zit
   * hierboven (eigen grondslag per onderdeel, één vermogensband): die brengt
   * hetzelfde geval op € 36.000, ruim onder de basis van € 90.000. Deze grens
   * vuurt daar dus niet; hij staat er zodat een nieuwe of gewijzigde rij in
   * `sub_bedragen` dat percentage nooit meer kan overschrijden.
   *
   * `investering_totaal` slokt zijn eigen onderdelen op — anders zou een
   * regeling die zowel over het totaal als over de batterij rekent een te ruime
   * basis krijgen. Rijen zonder euro-grondslag ('vast', 'per_kwh') leveren geen
   * basis; staat er dan niets, dan blijft alleen het percentageplafond over,
   * precies zoals vandaag (SPULA: 86.000 ruw → 40.000 door 20% van 200.000).
   */
  const basis = subsidiabeleBasis(gebruikteGrondslagen);
  let totaal = gedeeld + eigenMaximum;
  let basisgrensBeet = false;
  if (basis > 0 && totaal > basis) {
    notities.push(
      `De berekening kwam uit op € ${Math.round(totaal).toLocaleString('nl-NL')} terwijl de subsidiabele `
      + `kosten € ${Math.round(basis).toLocaleString('nl-NL')} zijn. Meer dan 100% van de kosten kan een `
      + 'regeling niet uitkeren; het bedrag is daarop teruggezet. Controleer de bedrag-rijen van deze regeling.',
    );
    totaal = basis;
    basisgrensBeet = true;
  }

  /**
   * DE SPECIFICATIE MOET OPTELLEN TOT HET TOTAAL.
   *
   * `breakdown` wordt per rij gevuld, dus vóór het percentageplafond en vóór de
   * sanity-grens hierboven. Bijt een van die twee, dan staat er een specificatie
   * die hoger uitkomt dan het bedrag eronder — gemeten met één duplicaat van de
   * SPULA-rij 350_550kw op intake 6daab7ab: totaal € 270.000, specificatie
   * € 384.000. Wie die twee naast elkaar legt kan het verschil niet plaatsen, en
   * een aftopping die je niet ziet is niet te controleren.
   *
   * Vandaar één expliciete regel met het negatieve bedrag, in plaats van de
   * rijen proportioneel schalen: schalen zou elk onderdeel een bedrag geven dat
   * nergens uit volgt, en dat is precies wat een specificatie niet mag doen.
   * `SubsidieDashboard.tsx:176-180` rendert elke sleutel met `formatCurrency`,
   * dus een negatieve regel toont gewoon als "-€ 114.000".
   *
   * Alleen wanneer een van de twee grenzen daadwerkelijk heeft gebeten — niet op
   * afrondingsruis: elke rij rondt apart af, dus zonder die vlaggen zou hier een
   * regel van één euro kunnen verschijnen.
   */
  if (percentageplafondBeet || basisgrensBeet) {
    const gespecificeerd = Object.values(breakdown).reduce((som, x) => som + x, 0);
    const verschil = Math.round(totaal) - gespecificeerd;
    if (verschil !== 0) {
      breakdown[basisgrensBeet ? AFTOPPING_BASIS : AFTOPPING_PLAFOND] = verschil;
    }
  }

  return { totaal: Math.round(totaal), breakdown, notities };
}

/** De twee regels waarmee de specificatie op het totaal uitkomt. Ook de test hangt hieraan. */
export const AFTOPPING_PLAFOND = 'Aftopping op het percentageplafond van de regeling';
export const AFTOPPING_BASIS = 'Aftopping op de subsidiabele kosten';

/** Velden die samen ín `investering_totaal` zitten en dus niet dubbel tellen. */
const ONDERDELEN_VAN_INVESTERING_TOTAAL = new Set([
  'investering_batterij',
  'investering_ems',
  'investering_laadinfra',
]);

function subsidiabeleBasis(gebruikt: Map<string, number>): number {
  const heeftTotaal = gebruikt.has('investering_totaal');
  let som = 0;
  for (const [veld, waarde] of gebruikt) {
    if (heeftTotaal && ONDERDELEN_VAN_INVESTERING_TOTAAL.has(veld)) continue;
    som += waarde;
  }
  return som;
}

// SPULA vermogen ranges (totaal vermogen laadstation)
const SPULA_RANGES: Array<{ sub: string; min: number; max: number }> = [
  { sub: '200_350kw', min: 200, max: 350 },
  { sub: '350_550kw', min: 350, max: 550 },
  { sub: '>=550kw', min: 550, max: Infinity },
];

// SPRILA DC ranges (totaal vermogen laadstation)
const SPRILA_DC_RANGES: Array<{ sub: string; min: number; max: number }> = [
  { sub: 'dc_>=550kw', min: 550, max: Infinity },
  { sub: 'dc_>=350kw', min: 350, max: 550 },
  { sub: 'dc_>=220kw', min: 220, max: 350 },
  { sub: 'dc_>=150kw', min: 150, max: 220 },
  { sub: 'dc_>=100kw', min: 100, max: 150 },
  { sub: 'dc_>=50kw', min: 50, max: 100 },
  { sub: 'dc_>=20kw', min: 20, max: 50 },
];

// SPRILA AC ranges (vermogen per laadpunt)
const SPRILA_AC_RANGES: Array<{ sub: string; min: number; max: number; duo?: boolean }> = [
  { sub: 'ac_duo_>=2x43kw', min: 43, max: Infinity, duo: true },
  { sub: 'ac_>=43kw', min: 43, max: Infinity },
  { sub: 'ac_duo_>=2x11kw', min: 11, max: 43, duo: true },
  { sub: 'ac_>=11kw', min: 11, max: 43 },
];

export function matchesVermogenRange(kw: number, stationType: string, subcategorie: string | null): boolean {
  if (!subcategorie) return true;

  // SPULA ranges
  for (const r of SPULA_RANGES) {
    if (subcategorie === r.sub) return kw >= r.min && kw < r.max;
  }

  // SPRILA DC ranges (match on total station power)
  if (stationType === 'DC') {
    for (const r of SPRILA_DC_RANGES) {
      if (subcategorie === r.sub) return kw >= r.min && kw < r.max;
    }
  }

  // SPRILA AC ranges (match on power per charging point)
  if (stationType === 'AC') {
    for (const r of SPRILA_AC_RANGES) {
      if (subcategorie === r.sub) return kw >= r.min && kw < r.max;
    }
  }

  return false;
}

function matchesTruckCategory(truck: { categorie: string; type: string }, cat: string): boolean {
  if (cat === 'truck_n2_>=10t') return truck.categorie === 'N2';
  if (cat === 'truck_n3_bakwagen') return truck.categorie === 'N3' && truck.type === 'bakwagen';
  if (cat === 'truck_n3_trekker') return truck.categorie === 'N3' && truck.type === 'trekker';
  return false;
}

function leesOptioneelGetal(data: SubsidieIntakeData, veld: string): number | null {
  const waarde = getNestedValue(data, veld);
  return typeof waarde === 'number' && Number.isFinite(waarde) ? waarde : null;
}

/**
 * Tekst die een ongetoetste regeling meekrijgt; ook de test hangt hieraan.
 *
 * De eerste zin spreekt met opzet de badge tegen. Een ongetoetste regeling
 * krijgt status 'gedeeltelijk' (de enige van de vier toegestane waarden die
 * past, zie `evaluateAllSubsidies`) en `SubsidieDashboard.tsx:18` plakt daar het
 * label "Data incompleet" op. Bij MIA/VAMIL is de intake juist compleet — wat
 * ontbreekt zijn de toetsingsregels in `sub_regels` (gemeten: 0 regels, tegen
 * SPULA 6, FLEX_E 3, SPRILA 2, EIA 1). Het dashboard rendert álle aanbevelingen
 * onder de badge (`SubsidieDashboard.tsx:196-204`), dus deze zin staat er direct
 * naast. Het label zelf rechtzetten vergt een tweede label voor het ongetoetste
 * geval in STATUS_CONFIG, en dat bestand valt buiten deze reparatie.
 */
export const AANBEVELING_GEEN_REGELS =
  'Niet de intake is incompleet, maar de toetsing: voor deze regeling staat geen enkele voorwaarde '
  + 'in de database, dus er is niets getoetst. Het bedrag is een rekensom, geen match. '
  + 'Controleer de voorwaarden handmatig bij RVO.';

// --- Main evaluation function ---

export function evaluateAllSubsidies(
  data: SubsidieIntakeData,
  regelingen: SubsidieRegeling[],
  regels: SubsidieRegel[],
  bedragen: SubsidieBedrag[],
): SubsidieEvaluatieResultaat[] {
  const results = regelingen.map(regeling => {
    const regelingRegels = regels.filter(r => r.regeling_id === regeling.id);
    const onvervuld: Array<{ beschrijving: string; foutmelding: string }> = [];
    const aanbevelingen: string[] = [];
    let hasDataGap = false;

    for (const regel of regelingRegels) {
      const fieldValue = getNestedValue(data, regel.veld);
      const isUndefined = fieldValue === undefined || fieldValue === null;

      if (isUndefined && regel.verplicht) {
        hasDataGap = true;
        aanbevelingen.push(`Vul "${regel.beschrijving}" in om te checken`);
        continue;
      }

      if (!isUndefined && !evaluateRule(data, regel)) {
        onvervuld.push({
          beschrijving: regel.beschrijving,
          foutmelding: regel.foutmelding ?? 'Niet voldaan',
        });
      }
    }

    /**
     * DEFECT 2 — een regeling zonder regels heette onvoorwaardelijk 'volledig'.
     *
     * Met nul regels draaide de lus hierboven nul keer, bleef `onvervuld` leeg
     * en `hasDataGap` false, en viel de statusbepaling door naar 'volledig'.
     * Alle vier de MIA/VAMIL-rijen in `sub_matches` bewijzen het: match_status
     * 'volledig', onvervulde_regels []. MIA/VAMIL is de enige regeling met nul
     * regels (gemeten: SPULA 6, FLEX_E 3, SPRILA 2, EIA 1, AANZET 1).
     *
     * Nul regels betekent "niet getoetst", niet "geslaagd". Dat verdient een
     * eigen status, maar die kan er nu niet komen: `sub_matches.match_status`
     * heeft een CHECK op vier waarden en `SubsidieDashboard.tsx:131` leest
     * `STATUS_CONFIG[match_status].icon` — een vijfde waarde zou de insert
     * weigeren en het dashboard laten crashen. Beide bestanden staan buiten deze
     * reparatie. Daarom: 'gedeeltelijk' (de bestaande status voor "niet volledig
     * te toetsen") plus de losse vlag `ongetoetst` en een aanbeveling die
     * zegt dát de regels ontbreken. Welke regels het zouden moeten zijn is
     * onbekend en wordt hier niet verzonnen.
     */
    const ongetoetst = regelingRegels.length === 0;
    if (ongetoetst) {
      aanbevelingen.push(AANBEVELING_GEEN_REGELS);
    }

    let match_status: SubsidieMatchStatus;
    if (onvervuld.length > 0) {
      match_status = 'niet_voldaan';
    } else if (ongetoetst || hasDataGap) {
      match_status = 'gedeeltelijk';
    } else {
      match_status = 'volledig';
    }

    // Skip closed/exhausted regelingen (but allow 'gepland' for pre-calculation)
    if (regeling.status === 'gesloten' || regeling.status === 'uitgeput') {
      match_status = 'niet_relevant';
    }

    const { totaal, breakdown, notities } = match_status !== 'niet_voldaan'
      ? calculateAmount(data, regeling, bedragen)
      : { totaal: 0, breakdown: {}, notities: [] as string[] };

    // Wat de rekenkern onderweg heeft moeten laten liggen — een ontbrekende
    // kostengrondslag, een letter zonder percentage — hoort de gebruiker te
    // zien, anders is het bedrag stil lager zonder uitleg.
    aanbevelingen.push(...notities);

    // Fallback: matched but €0 — explain why
    if ((match_status === 'volledig' || match_status === 'gedeeltelijk') && totaal === 0) {
      const hasInvestering = (data.investering_totaal ?? 0) > 0;
      const hasLaadstations = (data.laadstations ?? []).length > 0;
      if (!hasInvestering && !hasLaadstations) {
        aanbevelingen.push('Vul investeringsbedragen en/of laadstations in voor een schatting');
      } else if (regeling.code === 'MIA_VAMIL' && !hasInvestering) {
        aanbevelingen.push('Vul investeringsbedrag in — MIA/VAMIL is een fiscale aftrek op de investering');
      } else {
        aanbevelingen.push('Gebruik de RVO rekentool voor exacte bedragen');
      }
    }

    return {
      regeling,
      match_status,
      geschat_totaal: totaal,
      berekening: breakdown,
      onvervulde_regels: onvervuld,
      aanbevelingen,
      ongetoetst,
    };
  });

  // EIA/MIA conflict warning
  //
  // Let op: dit is de ENIGE stapelbaarheidscontrole in de engine, en hij is
  // hardgecodeerd. De tabel `sub_combinaties` wordt hier niet gelezen — alleen
  // de weergavematrix in `CRMSubsidies.tsx:299` doet dat — en
  // `min_subsidiebedrag` evenmin.
  //
  // Gemeten op 8-9-2026: `sub_combinaties` telt 12 rijen voor 2026, waarvan er
  // vijf `combineerbaar = false` zijn: SPRILA↔SPULA, SPRILA↔FLEX_E,
  // SPRILA↔MIA_VAMIL, SPULA↔FLEX_E en EIA↔MIA_VAMIL. Alleen de laatste komt
  // hieronder ter sprake. De eerste vier zijn contante subsidies die
  // `SubsidieDashboard.tsx:77-79` vandaag gewoon bij elkaar optelt, zonder
  // waarschuwing. Repareren kan hier niet: `evaluateAllSubsidies` krijgt de
  // combinaties niet binnen en alle vier de aanroepers staan buiten deze
  // reparatie. De sleutel is bovendien de regeling-CODE (tekst), niet het id.
  const eiaMatch = results.find(r => r.regeling.code === 'EIA' && r.geschat_totaal > 0);
  const miaMatch = results.find(r => r.regeling.code === 'MIA_VAMIL' && r.geschat_totaal > 0);
  if (eiaMatch && miaMatch) {
    const warning = 'Let op: EIA en MIA zijn niet combineerbaar op hetzelfde bedrijfsmiddel. Kies de gunstigste optie.';
    // `unshift` en niet `push`: de Subsidies-tab van de Laadplein Designer toont
    // per kaart maar één aanbeveling (`SubsidieTab.tsx:216` — `slice(0, 1)`).
    // Zolang deze waarschuwing achteraan stond, verdween hij op de MIA-kaart
    // achter de geen-regels-melding, terwijl dit de enige controle in de engine
    // is die twee regelingen tegen elkaar afzet — op intake 6daab7ab gaat het om
    // EIA 102.600 naast MIA 89.505, waarvan er maar één mag worden geclaimd.
    // Het dashboard toont álle aanbevelingen, dus daar blijft de rest zichtbaar.
    eiaMatch.aanbevelingen.unshift(warning);
    miaMatch.aanbevelingen.unshift(warning);
  }

  return results;
}
